/**
 * Current Boostt staff member's own per-user API token — fetched from
 * user-login-svc's `GET /api/me/token` (session-cookie authenticated, same
 * cross-domain Boostt session the browser already holds for every other
 * `*.boostt.ai` service) and cached in memory for the lifetime of the page.
 *
 * Why this exists: mailhouse-svc's `/admin/*` routes no longer accept a
 * Boostt session cookie at all (see `mailhouse-svc/src/presentation/
 * middleware/session_auth.py`). They accept only `X-Master-Token`
 * (service-to-service, never usable from a browser) or `Authorization:
 * Bearer <token>` + `X-User-Id: <id>` — a Boostt staff member's own API
 * token. `MailhouseAxios.tsx` uses `getMyApiToken()` below to attach that
 * pair to every outgoing request.
 *
 * Caching: deliberately an in-memory module-level variable, NOT
 * localStorage/sessionStorage — a bearer token sitting in web storage is a
 * bigger XSS blast radius than a token that vanishes on reload. Refetching
 * once per page load is cheap; `GET /me/token` is a lightweight lookup, not
 * a mint operation.
 *
 * Failure handling: if `/api/me/token` itself fails (staff member has no token
 * provisioned yet, or their Boostt session is not valid), the promise
 * rejects and the cache is cleared so a later retry (e.g. after logging in,
 * or after an admin provisions their token) isn't stuck replaying the same
 * failure forever. Callers (the MailhouseAxios request interceptor) are
 * expected to swallow the rejection and let the request go out without the
 * auth headers — mailhouse-svc will then return a clean 401, surfaced by the
 * same error handling as any other API failure.
 */

import axios from 'axios';
import { apiConfig } from '@/config/apiConfig';

export interface MyApiToken {
  token: string;
  user_id: number;
}

let cachedTokenPromise: Promise<MyApiToken> | null = null;

/**
 * Resolves to the current staff member's `{ token, user_id }`. Safe to call
 * repeatedly/concurrently — the first call in flight is shared by every
 * caller until it settles; a failure clears the cache so the next call
 * retries instead of replaying a stale rejection.
 */
export function getMyApiToken(): Promise<MyApiToken> {
  if (!cachedTokenPromise) {
    cachedTokenPromise = axios
      .get<MyApiToken>(`${apiConfig.baseUrls.userLogin}/api/me/token`, {
        withCredentials: true,
      })
      .then((res) => res.data)
      .catch((err) => {
        cachedTokenPromise = null;
        throw err;
      });
  }
  return cachedTokenPromise;
}

/**
 * Clears the cached token/in-flight request so the next `getMyApiToken()`
 * call refetches from scratch. Not currently wired to logout anywhere
 * (page reload already clears the module-level cache), but exposed for
 * completeness / future use (e.g. an explicit "switch user" flow).
 */
export function clearCachedMyApiToken(): void {
  cachedTokenPromise = null;
}
